<template>
  <section :class="[wrapperClass, 'overflow-x-clip']">
    <div class="px-4 mx-auto max-w-7xl sm:px-6">
      <div
        class="overflow-hidden relative rounded-2xl shadow-2xl transition-transform duration-700 ease-out will-change-transform"
        :style="dashboardStyle"
        ref="dashboardEl"
      >
        <div
          class="absolute inset-0 bg-gradient-to-br from-[#006086]/5 to-[#004d6b]/5"
        ></div>
        <img
          class="relative w-full"
          :src="src"
          :srcset="srcset"
          sizes="(min-width: 1280px) 1200px, (min-width: 640px) calc(100vw - 48px), calc(100vw - 32px)"
          :alt="alt"
          loading="lazy"
          width="1920"
          height="1080"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import dashboardIllustration from "@/assets/dashboard.webp";
import dashboardIllustrationSmall from "@/assets/dashboard-sm.webp";
import { ref, onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  imageSrc: { type: String, default: "dashboardIllustration" },
  alt: { type: String, default: "Tela de Dashboard Kali Gestão" },
  maxScroll: { type: Number, default: 160 },
  initialScale: { type: Number, default: 0.82 },
  scaleIncrease: { type: Number, default: 0.28 },
  initialTranslateY: { type: Number, default: 90 },
  initialOpacity: { type: Number, default: 0.55 },
  wrapperClass: {
    type: String,
    default: "relative -mt-6 sm:-mt-20 pb-8 sm:pb-12 z-[5]",
  },
  // Mantido para compatibilidade, mas não será usado para desabilitar em mobile
  disableOnMobileWidth: { type: Number, default: 0 },
});

const isDefaultImage = computed(
  () => !props.imageSrc || props.imageSrc === "dashboardIllustration",
);
const src = computed(() =>
  isDefaultImage.value ? dashboardIllustration : props.imageSrc,
);
const srcset = computed(() =>
  isDefaultImage.value
    ? `${dashboardIllustrationSmall} 700w, ${dashboardIllustration} 1200w`
    : undefined,
);

const dashboardEl = ref(null);
const dashboardStyle = ref({});

// Flag para tablets/smartphones (não desabilita animação, apenas ajusta desempenho)
const isMobile = ref(false);
const maxScale = ref(props.initialScale + props.scaleIncrease);
const updateConstraints = () => {
  const w = window.innerWidth || document.documentElement.clientWidth;
  isMobile.value = w < 1024; // considera até 1023 como dispositivos móveis/tablets
  const constrainedMaxScale =
    w < 1280 ? 1 : props.initialScale + props.scaleIncrease;
  maxScale.value = Math.max(props.initialScale, constrainedMaxScale);
};

// rAF para suavizar em dispositivos móveis
let ticking = false;
const updateStyle = () => {
  if (!dashboardEl.value) return;
  // Start transition as soon as the page begins to scroll
  const raw = Math.min(Math.max(window.scrollY / props.maxScroll, 0), 1);
  const eased = 1 - Math.pow(1 - raw, 2); // ease-out
  const scale = Math.min(
    props.initialScale + eased * props.scaleIncrease,
    maxScale.value,
  );
  const translateY = (1 - eased) * props.initialTranslateY;
  const opacity = props.initialOpacity + eased * (1 - props.initialOpacity);
  dashboardStyle.value = {
    transform: `translateY(${translateY}px) scale(${scale})`,
    opacity,
  };
};

const onScroll = () => {
  if (!ticking) {
    window.requestAnimationFrame(() => {
      updateStyle();
      ticking = false;
    });
    ticking = true;
  }
};

onMounted(() => {
  updateConstraints();
  updateStyle();
  // Habilitar o listener de scroll para todos os dispositivos
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", updateConstraints, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", updateConstraints);
});
</script>
